import React from "react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "danger" | "ghost";
export type ButtonSize = "sm" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  iconOnly?: boolean;
  children?: React.ReactNode;
}

const base: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",       // 12px — confirmed from Figma
  fontWeight: "var(--font-weight-bold)",        // 700
  lineHeight: "var(--line-height-label-s)",     // 16px
  letterSpacing: "-0.24px",
  borderRadius: "var(--radius-s)",              // 8px — confirmed
  border: "1px solid transparent",
  cursor: "pointer",
  textDecoration: "none",
  whiteSpace: "nowrap",
  transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
  outline: "none",
  userSelect: "none",
};

function getSizeStyles(size: ButtonSize, iconOnly: boolean): React.CSSProperties {
  if (size === "lg") {
    return iconOnly
      ? { height: "40px", padding: "0 var(--spacing-m)" }
      : { height: "40px", padding: "0 var(--spacing-s)" };
  }
  return { padding: "var(--spacing-s)" };                         // 8px all sides
}

function getVariantStyles(variant: ButtonVariant): {
  default: React.CSSProperties;
  hover: React.CSSProperties;
  disabled: React.CSSProperties;
} {
  switch (variant) {
    case "primary":
      return {
        default:  { background: "var(--color-bg-brand-dark)", color: "var(--color-content-inverse)" },
        hover:    { background: "var(--color-bg-secondary)",  color: "var(--color-content-inverse)" },
        disabled: { background: "var(--color-border-secondary)", color: "var(--color-content-inverse)", cursor: "not-allowed" },
      };
    case "secondary":
      return {
        default:  { background: "var(--color-bg-brand-lightest)", color: "var(--color-content-brand)" },
        hover:    { background: "var(--primitive-blue-20)",        color: "var(--color-content-brand)" },  // #CDD1FE
        disabled: { background: "var(--color-border-secondary)",   color: "var(--color-content-inverse)", cursor: "not-allowed" },
      };
    case "outline":
      return {
        default:  { background: "var(--color-bg-primary)",  borderColor: "var(--color-border-primary)", color: "var(--color-content-primary)" },
        hover:    { background: "var(--color-bg-primary)",  borderColor: "var(--color-border-brand)",   color: "var(--color-content-brand)" },
        disabled: { background: "var(--color-bg-primary)",  borderColor: "var(--color-border-primary)", color: "var(--color-content-disabled)", cursor: "not-allowed" },
      };
    case "danger":
      return {
        default:  { background: "var(--color-bg-primary)", borderColor: "var(--color-border-danger)", color: "var(--color-content-danger)" },
        hover:    { background: "var(--color-bg-danger)",  borderColor: "var(--color-bg-danger)",     color: "var(--color-content-inverse)" },
        disabled: { background: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)", color: "var(--color-content-disabled)", cursor: "not-allowed" },
      };
    case "ghost":
      return {
        default:  { background: "transparent", borderColor: "transparent", color: "var(--color-content-brand)",   padding: "var(--spacing-2xs)" },
        hover:    { background: "transparent", borderColor: "transparent", color: "var(--color-content-primary)", padding: "var(--spacing-2xs)" },
        disabled: { background: "transparent", borderColor: "transparent", color: "var(--color-content-disabled)", padding: "var(--spacing-2xs)", cursor: "not-allowed" },
      };
  }
}

export function Button({
  variant = "primary",
  size = "lg",
  leadingIcon,
  trailingIcon,
  iconOnly = false,
  children,
  disabled,
  style,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: ButtonProps) {
  const [hovered, setHovered] = React.useState(false);

  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size, iconOnly);
  const stateStyles = disabled
    ? variantStyles.disabled
    : hovered
    ? variantStyles.hover
    : variantStyles.default;

  const gap = leadingIcon || trailingIcon ? "var(--spacing-2xs)" : undefined; // 4px

  const computedStyle: React.CSSProperties = {
    ...base,
    ...sizeStyles,
    ...stateStyles,
    gap,
    ...style,
  };

  return (
    <button
      disabled={disabled}
      style={computedStyle}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e); }}
      {...rest}
    >
      {leadingIcon && (
        <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, width: 16, height: 16 }}>
          {leadingIcon}
        </span>
      )}
      {!iconOnly && children && (
        <span style={{ paddingInline: "var(--spacing-2xs)" }}>{children}</span>
      )}
      {iconOnly && (
        <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, width: 16, height: 16 }}>
          {children}
        </span>
      )}
      {trailingIcon && (
        <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, width: 16, height: 16 }}>
          {trailingIcon}
        </span>
      )}
    </button>
  );
}
